import React, { useEffect, useMemo, useState, useCallback } from 'react'
import StatusPill from '../components/StatusPill'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import {
  detectOldSystemExport,
  LEGACY_IMPORT_COLUMNS,
  legacyImportTemplateCsv,
  parseLegacyCsv,
} from '../lib/legacyImport'
import { oldSystemCustomerPayload, parseOldSystemCustomers, reviewOldSystemCustomers } from '../lib/legacyCustomerImport'
import { HISTORIC_FIELDS, historicJobPayload, historicalCustomerCandidates, historicalOriginalCustomer, parseHistoricJobs, reviewHistoricJobs } from '../lib/legacyHistoricJobImport'
import { legacyServicePayload, reviewLegacyServices } from '../lib/legacyServiceImport'
import { proposalCsv } from '../lib/legacyAiAssist'
import LegacyAiAssist from '../components/LegacyAiAssist'
import LegacyHistoricJobModal from '../components/LegacyHistoricJobModal'
import HistoricImportLinkReview from '../components/HistoricImportLinkReview'
import { fmtDate } from '../lib/utils'

function norm(value) {
  return String(value || '').trim().toLowerCase()
}

function customerName(customer = {}) {
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || 'Customer'
}

function bikeName(bike = {}) {
  return [bike.year, bike.brand, bike.model].filter(Boolean).join(' ') || ''
}

function componentName(component = {}) {
  return [component.brand, component.model].filter(Boolean).join(' ') || component.type || ''
}

function serviceSummary(record = {}) {
  return record.service_title || record.service_description || 'Legacy service'
}

function downloadTemplate() {
  const blob = new Blob([legacyImportTemplateCsv()], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'rebound_legacy_history_template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

function structuredImportSource() {
  return {
    sourceType: 'structured_csv',
    metadata: { milestone: '6A', import_model: 'legacy_reference_history', lane: 'structured_csv' },
  }
}

async function loadAllWorkshopRows(query) {
  const rows = []
  for (let page = 0; page < 20; page++) {
    const { data, error } = await query().range(page * 1000, page * 1000 + 999)
    if (error) return { data: [], error }
    rows.push(...(data || []))
    if ((data || []).length < 1000) return { data: rows, error: null }
  }
  return { data: [], error: new Error('Existing-record matching exceeds the safe preview limit.') }
}

export default function LegacyImport() {
  const { workshopId, profile } = useAuth()
  const [customers, setCustomers] = useState([])
  const [records, setRecords] = useState([])
  const [opportunities, setOpportunities] = useState([])
  const [loading, setLoading] = useState(true)
  const [fileName, setFileName] = useState('')
  const [csvText, setCsvText] = useState('')
  const [previewRows, setPreviewRows] = useState([])
  const [previewSource, setPreviewSource] = useState(structuredImportSource)
  const [parseError, setParseError] = useState('')
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState(null)
  const [historySearch, setHistorySearch] = useState('')
  const [customerCsvText, setCustomerCsvText] = useState('')
  const [customerFileName, setCustomerFileName] = useState('')
  const [customerPreview, setCustomerPreview] = useState(null)
  const [customerError, setCustomerError] = useState('')
  const [customerImportResult, setCustomerImportResult] = useState(null)
  const [customerReviewed, setCustomerReviewed] = useState(false)
  const [customerSkippedRows, setCustomerSkippedRows] = useState([])
  const [customerImporting, setCustomerImporting] = useState(false)
  const [customerMatchesReady, setCustomerMatchesReady] = useState(false)
  const [historicMatchesReady, setHistoricMatchesReady] = useState(false)
  const [serviceMatchesReady, setServiceMatchesReady] = useState(false)
  const [historicRecords, setHistoricRecords] = useState([])
  const [historicText, setHistoricText] = useState('')
  const [historicFileName, setHistoricFileName] = useState('')
  const [historicPreview, setHistoricPreview] = useState(null)
  const [historicError, setHistoricError] = useState('')
  const [historicSkipped, setHistoricSkipped] = useState([])
  const [historicReviewed, setHistoricReviewed] = useState(false)
  const [historicImporting, setHistoricImporting] = useState(false)
  const [historicResult, setHistoricResult] = useState(null)
  const [historicSearch, setHistoricSearch] = useState('')
  const [viewHistoricId, setViewHistoricId] = useState(null)
  const [serviceReviewed, setServiceReviewed] = useState(false)

  const load = useCallback(async () => {
    if (!workshopId) return
    setLoading(true)
    const [customerRows, legacyRows, opportunityRows, historicRows] = await Promise.all([
      loadAllWorkshopRows(() => supabase.from('customers').select('*').eq('workshop_id', workshopId)
        .order('last_name').order('id')),
      loadAllWorkshopRows(() => supabase
        .from('legacy_service_records')
        .select('*, customer:customers(first_name,last_name,email,phone), bike:bikes(brand,model,year,serial), component:components(type,brand,model,serial)')
        .eq('workshop_id', workshopId)
        .order('service_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false }).order('id')),
      supabase
        .from('legacy_service_opportunities')
        .select('*')
        .eq('workshop_id', workshopId)
        .eq('status', 'suggested')
        .order('suggested_due_date', { ascending: true }),
      loadAllWorkshopRows(() => supabase.from('legacy_historic_job_records').select('*').eq('workshop_id', workshopId)
        .order('created_at', { ascending: false }).order('id')),
    ])
    setCustomers(customerRows.data || [])
    setCustomerMatchesReady(!customerRows.error)
    setRecords(legacyRows.data || [])
    setServiceMatchesReady(!legacyRows.error)
    setOpportunities(opportunityRows.data || [])
    setHistoricRecords(historicRows.data || [])
    setHistoricMatchesReady(!historicRows.error)
    setLoading(false)
  }, [workshopId])

  useEffect(() => { load() }, [load])

  const enrichedRows = useMemo(() => reviewLegacyServices(previewRows, customers, records), [previewRows, customers, records])

  const rowStats = enrichedRows.reduce((stats, row) => {
    stats.total += 1
    stats[row.errors.length ? 'error' : row.warnings.length ? 'warning' : 'valid'] += 1
    return stats
  }, { total: 0, valid: 0, warning: 0, error: 0 })

  const canImport = profile?.role === 'owner' && serviceMatchesReady && customerMatchesReady
    && serviceReviewed && enrichedRows.length > 0
    && enrichedRows.length <= 200 && enrichedRows.every(row => !row.errors.length) && !importing
  const reviewedCustomers = useMemo(() => reviewOldSystemCustomers(customerPreview?.rows || [], customers, customerSkippedRows), [customerPreview, customers, customerSkippedRows])
  const blockedCustomers = reviewedCustomers.filter(row => row.errors.length)
  const customerCounts = reviewedCustomers.reduce((counts, row) => {
    counts[row.action] += 1
    return counts
  }, { create: 0, reuse: 0, skip: 0, blocked: 0 })
  const canImportCustomers = profile?.role === 'owner' && customerMatchesReady && customerReviewed
    && reviewedCustomers.length <= 200 && customerCounts.create + customerCounts.reuse > 0
    && blockedCustomers.length === 0 && !customerImporting
  const reviewedHistoric = useMemo(() => reviewHistoricJobs(historicPreview?.rows || [], historicRecords, historicSkipped),
    [historicPreview, historicRecords, historicSkipped])
  const historicBlocked = reviewedHistoric.filter(row => row.errors.length)
  const historicCounts = reviewedHistoric.reduce((counts, row) => { counts[row.action] += 1; return counts },
    { create: 0, reuse: 0, skip: 0, blocked: 0 })
  const linkedHistoricCount = historicRecords.filter(record => record.customer_id).length
  const canImportHistoric = profile?.role === 'owner' && historicMatchesReady && historicReviewed && reviewedHistoric.length > 0
    && reviewedHistoric.length <= 200 && historicBlocked.length === 0
    && historicCounts.create + historicCounts.reuse > 0 && !historicImporting

  const filteredRecords = useMemo(() => {
    const q = norm(historySearch)
    if (!q) return records
    return records.filter(record =>
      norm(record.search_text).includes(q)
      || norm(customerName(record.customer)).includes(q)
      || norm(bikeName(record.bike)).includes(q)
      || norm(componentName(record.component)).includes(q)
    )
  }, [records, historySearch])

  function previewCsv(text, nextFileName = fileName, sourceDetails = null, { updateCsvText = true } = {}) {
    const exportType = detectOldSystemExport(text)
    if (exportType === 'customers') {
      previewCustomerCsv(text, nextFileName)
      setParseError('Customer-only exports belong in the Customer CSV Import lane below, not service history.')
      setPreviewRows([])
      return
    }
    if (exportType === 'historic_jobs') {
      previewHistoricJobs(text, nextFileName)
      setParseError('Historical jobs belong in the Historical Jobs preview below. No active jobs are created.')
      setPreviewRows([])
      return
    }
    const parsed = parseLegacyCsv(text)
    setFileName(nextFileName)
    if (updateCsvText) setCsvText(text)
    setPreviewSource(sourceDetails || structuredImportSource())
    setResult(null)
    if (parsed.errors.length) {
      setParseError(parsed.errors.join(' '))
      setPreviewRows([])
      return
    }
    setParseError('')
    setPreviewRows(parsed.rows)
    setServiceReviewed(false)
  }

  function previewCustomerCsv(text, nextFileName = customerFileName, sourceNumbers = []) {
    const parsed = parseOldSystemCustomers(text)
    setCustomerCsvText(text)
    setCustomerFileName(nextFileName)
    setCustomerPreview(parsed.errors.length ? null : { ...parsed, rows: parsed.rows.map((row, index) => ({
      ...row, rowNumber: sourceNumbers[index] || row.rowNumber,
    })) })
    setCustomerError(parsed.errors.join(' '))
    setCustomerReviewed(false)
    setCustomerSkippedRows([])
    setCustomerImportResult(null)
  }

  function previewHistoricJobs(text, nextFileName = historicFileName, sourceNumbers = [], sourceRows = []) {
    const parsed = parseHistoricJobs(text)
    const ignoredColumns = sourceRows.length
      ? [...new Set(sourceRows.flatMap(row => row.ignored_fields || []))] : parsed.ignoredColumns
    setHistoricText(text)
    setHistoricFileName(nextFileName)
    setHistoricPreview(parsed.errors.length ? null : { ...parsed, ignoredColumns,
      rows: parsed.rows.map((row, index) => ({
      ...row, rowNumber: sourceNumbers[index] || row.rowNumber,
      sourceFields: sourceRows[index]?.source_fields || row.sourceFields,
      ignoredFields: sourceRows[index]?.ignored_fields || [],
      uncertainties: sourceRows[index]?.uncertainties || [],
    })) })
    setHistoricError(parsed.errors.join(' '))
    setHistoricSkipped([])
    setHistoricReviewed(false)
    setHistoricResult(null)
  }

  async function handleHistoricFile(event) {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      if (file.size > 500000) throw new Error('Historical job file must be under 500 KB.')
      previewHistoricJobs(await file.text(), file.name)
    } catch (err) {
      setHistoricError(err.message)
      setHistoricPreview(null)
    } finally {
      event.target.value = ''
    }
  }

  async function importHistoricBatch() {
    if (!canImportHistoric) return
    setHistoricImporting(true)
    setHistoricError('')
    try {
      const { data, error } = await supabase.rpc('import_legacy_historic_jobs', {
        p_workshop_id: workshopId, p_rows: historicJobPayload(reviewedHistoric),
      })
      if (error) throw error
      setHistoricResult(data)
      setHistoricReviewed(false)
      await load()
    } catch (err) {
      setHistoricError(err.message || 'Historical references were not saved.')
    } finally {
      setHistoricImporting(false)
    }
  }

  function customerProposalCsv(rows) {
    return proposalCsv(['first_name', 'last_name', 'email', 'mobile', 'notes'], rows.map(row => ({
      ...row, proposed: { ...row.proposed, mobile: row.proposed?.phone || '' },
    })))
  }

  function validateCustomerProposal(rows) {
    const parsed = parseOldSystemCustomers(customerProposalCsv(rows))
    return parsed.errors.length ? rows.map(() => parsed.errors) : parsed.rows.map(row => row.errors)
  }

  function validateHistoricProposal(rows) {
    const parsed = parseHistoricJobs(proposalCsv(HISTORIC_FIELDS, rows))
    return parsed.errors.length ? rows.map(() => parsed.errors) : parsed.rows.map(row => row.errors)
  }

  function validateServiceProposal(rows) {
    const parsed = parseLegacyCsv(proposalCsv(LEGACY_IMPORT_COLUMNS, rows))
    return parsed.errors.length ? rows.map(() => parsed.errors) : parsed.rows.map(row => row.errors)
  }

  async function handleCustomerFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      if (file.size > 500000) throw new Error('Customer CSV must be under 500 KB.')
      previewCustomerCsv(await file.text(), file.name)
    } catch (err) {
      setCustomerError(err.message || 'Could not read that customer CSV.')
      setCustomerPreview(null)
    } finally {
      e.target.value = ''
    }
  }

  async function importCustomerBatch() {
    if (!canImportCustomers) return
    setCustomerImporting(true)
    setCustomerError('')
    try {
      const { data, error } = await supabase.rpc('import_legacy_customers', {
        p_workshop_id: workshopId,
        p_rows: oldSystemCustomerPayload(reviewedCustomers),
      })
      if (error) throw error
      setCustomerImportResult(data)
      setCustomerReviewed(false)
      await load()
    } catch (err) {
      setCustomerError(err.message || 'Customer import failed without committing the batch.')
    } finally {
      setCustomerImporting(false)
    }
  }

  function setCustomerSkip(rowNumber, skip) {
    setCustomerSkippedRows(current => skip
      ? [...current.filter(number => number !== rowNumber), rowNumber]
      : current.filter(number => number !== rowNumber))
    setCustomerReviewed(false)
    setCustomerImportResult(null)
  }

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      previewCsv(text, file.name)
    } catch {
      setParseError('Could not read that CSV file.')
      setPreviewRows([])
    } finally {
      e.target.value = ''
    }
  }

  async function importPreviewRows() {
    if (!canImport) return
    setImporting(true)
    setParseError('')
    try {
      const { data, error } = await supabase.rpc('import_reviewed_legacy_services', {
        p_workshop_id: workshopId,
        p_rows: legacyServicePayload(enrichedRows),
        p_source_type: previewSource.sourceType === 'ai_messy' ? 'ai_messy' : 'structured_csv',
      })
      if (error) throw error
      setResult(data)
      setServiceReviewed(false)
      await load()
    } catch (err) {
      setParseError(err.message || 'No service-history batch was saved.')
    } finally {
      setImporting(false)
    }
  }
  if (loading) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="page-title">Legacy Import</div>
          <div className="text-muted fs12 mt4">Import old customer and service history as searchable reference data.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={downloadTemplate}>CSV Template</button>
        </div>
      </div>

      <div className="legacy-import-grid">
        <section className="card">
          <div className="card-title">Service History</div>
          <div className="legacy-import-note">
            Legacy service rows create reference history only. They do not create jobs, workflow, payments, reports, job numbers, or QR tags.
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Upload CSV</label>
              <input type="file" accept=".csv,text/csv" onChange={handleFile} />
              {fileName && previewSource.sourceType === 'structured_csv' && <div className="text-muted fs12 mt4">Loaded: {fileName}</div>}
            </div>
            <div className="field">
              <label>Paste CSV</label>
              <textarea
                value={csvText}
                onChange={e => { setCsvText(e.target.value); setPreviewRows([]); setResult(null); setParseError('') }}
                placeholder="Paste the Rebound legacy CSV template rows here"
                style={{ minHeight: 82, fontFamily: 'monospace', fontSize: 12 }}
              />
            </div>
          </div>
          <div className="action-row" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" onClick={() => previewCsv(csvText, fileName)} disabled={!csvText.trim() || importing}>Preview CSV</button>
            {previewRows.length > 0 && <button className="btn btn-ghost" onClick={() => { setPreviewRows([]); setResult(null); setPreviewSource(structuredImportSource()) }}>Clear Preview</button>}
          </div>
          {parseError && <div className="alert alert-error" style={{ marginTop: 12 }}>{parseError}</div>}
          {(!serviceMatchesReady || !customerMatchesReady) && <div className="alert alert-error">Existing service or customer matches are incomplete. Review/import is blocked.</div>}
          {result && (
            <div className="alert alert-success" style={{ marginTop: 12 }}>
              Service history saved atomically: {result.created} created, {result.reused} reused. {result.idempotent ? 'This was a repeat batch.' : ''}
            </div>
          )}
          <LegacyAiAssist mode="service_history" title="Service History" workshopId={workshopId}
            sourceText={csvText} columns={LEGACY_IMPORT_COLUMNS} validateRows={validateServiceProposal}
            onUse={rows => {
              const parsed = parseLegacyCsv(proposalCsv(LEGACY_IMPORT_COLUMNS, rows))
              setPreviewRows(parsed.rows.map((row, index) => ({ ...row,
                rowNumber: rows[index].row_number,
                normalised: { ...row.normalised, row_number: rows[index].row_number },
              })))
              setPreviewSource({ sourceType: 'ai_messy', metadata: { lane: 'service_history' } })
              setServiceReviewed(false)
              setParseError(parsed.errors.join(' '))
            }} />
        </section>

        <section className="card">
          <div className="card-title">Reference History</div>
          <div className="legacy-import-stats">
            <span><strong>{records.length}</strong> legacy records</span>
            <span><strong>{opportunities.length}</strong> suggested opportunities</span>
          </div>
          <div className="search-wrap" style={{ marginBottom: 0 }}>
            <input value={historySearch} onChange={e => setHistorySearch(e.target.value)} placeholder="Search legacy history..." />
          </div>
        </section>

        <section className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-title">Customer CSV Import</div>
          <div className="legacy-import-note">
            Owner-reviewed customer records only. Reused customers are never updated or merged. Bikes, job counts, historic jobs, marketing preferences and service history are not imported.
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Old-system customer CSV</label>
              <input type="file" accept=".csv,text/csv" onChange={handleCustomerFile} />
              {customerFileName && <div className="text-muted fs12 mt4">Loaded: {customerFileName}</div>}
            </div>
            <div className="field">
              <label>Paste customer CSV</label>
              <textarea
                value={customerCsvText}
                onChange={e => {
                  setCustomerCsvText(e.target.value)
                  setCustomerPreview(null)
                  setCustomerReviewed(false)
                  setCustomerSkippedRows([])
                  setCustomerImportResult(null)
                  setCustomerError('')
                }}
                placeholder="First Name,Last Name,Email,Mobile,Landline,Notes"
                style={{ minHeight: 82, fontFamily: 'monospace', fontSize: 12 }}
              />
            </div>
          </div>
          <div className="action-row" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" onClick={() => previewCustomerCsv(customerCsvText)} disabled={!customerCsvText.trim() || customerImporting}>Preview Customers</button>
          </div>
          {!customerMatchesReady && <div className="alert alert-error" style={{ marginTop: 12 }}>Existing-customer matching is incomplete. Import is blocked; contact support.</div>}
          {customerError && <div className="alert alert-error" style={{ marginTop: 12 }}>{customerError}</div>}
          {customerImportResult && (
            <div className="alert alert-success" style={{ marginTop: 12 }}>
              Customer batch saved: {customerImportResult.created} created, {customerImportResult.reused} reused, {customerImportResult.skipped} skipped.
              {customerImportResult.idempotent ? ' This was an idempotent repeat.' : ''}
            </div>
          )}
          <LegacyAiAssist mode="customers" title="Customers" workshopId={workshopId}
            sourceText={customerCsvText} columns={['first_name', 'last_name', 'email', 'phone', 'notes']}
            validateRows={validateCustomerProposal}
            onUse={rows => previewCustomerCsv(customerProposalCsv(rows), 'AI customer proposal', rows.map(row => row.row_number))} />
          {customerPreview && (
            <div className="legacy-preview-card" style={{ marginTop: 14 }}>
              <div className="legacy-preview-head">
                <div>
                  <div className="card-title" style={{ marginBottom: 4 }}>Customer Preview</div>
                  <div className="text-muted fs12">
                    {reviewedCustomers.length} rows - {customerCounts.create} create - {customerCounts.reuse} reuse - {customerCounts.skip} skip - {customerCounts.blocked} blocked
                  </div>
                </div>
              </div>
              <div className="field-hint" style={{ marginBottom: 12 }}>
                Mapped: First Name, Last Name, Email, Mobile (or Landline if Mobile is blank), Notes.
                {customerPreview.unsupportedColumns.length > 0 && (
                  <> Unsupported columns (ignored): {customerPreview.unsupportedColumns.join(', ')}.</>
                )}
              </div>
              <div className="legacy-preview-table">
                {reviewedCustomers.map(row => (
                  <div key={row.rowNumber} className={`legacy-preview-row legacy-preview-${row.errors.length ? 'error' : 'valid'}`}>
                    <div className="legacy-preview-row-main">
                      <span className="job-status-chip">Row {row.rowNumber}</span>
                      <strong>{customerName(row.customer)}</strong>
                      <span>{row.customer.email || 'No email'}</span>
                      <span>{row.customer.phone || 'No phone'}</span>
                    </div>
                    <div className="legacy-preview-row-meta">
                      <span>{row.action === 'blocked' ? 'Blocked' : row.action === 'skip' ? 'Skip - no customer written' : row.match ? 'Reuse existing customer' : row.priorRows.length ? `Reuse source row ${row.priorRows[0]}` : 'Create customer'}</span>
                      {row.candidates.map(candidate => (
                        <span key={candidate.id}>Existing: {customerName(candidate)} ({candidate.email || candidate.phone || 'contact unavailable'})</span>
                      ))}
                      {row.priorRows.length > 0 && <span>Similar source rows: {row.priorRows.join(', ')}</span>}
                      {row.similarNames.map(candidate => (
                        <span key={candidate.id}>Same name, different contact: {customerName(candidate)} ({candidate.email || candidate.phone || 'contact unavailable'})</span>
                      ))}
                      {row.similarSourceRows.length > 0 && <span>Same name, different contact in source rows: {row.similarSourceRows.join(', ')}</span>}
                    </div>
                    {row.customer.notes && <div className="legacy-preview-row-meta" style={{ overflowWrap: 'anywhere' }}>Notes: {row.customer.notes}</div>}
                    {row.warnings?.length > 0 && <div className="legacy-preview-row-warnings">{row.warnings.map((message, index) => <span key={index}>{message}</span>)}</div>}
                    {row.errors.length > 0 && <div className="legacy-preview-row-warnings">{row.errors.map((message, index) => <span key={index}>{message}</span>)}</div>}
                    {row.ambiguous && (
                      <label className="field-hint" style={{ display: 'block', marginTop: 8 }}>
                        <input
                          type="checkbox"
                          checked={row.action === 'skip'}
                          onChange={e => setCustomerSkip(row.rowNumber, e.target.checked)}
                          disabled={profile?.role !== 'owner' || row.validationErrors.length > 0}
                        />
                        {' '}Skip this ambiguous row (no customer created or reused)
                      </label>
                    )}
                  </div>
                ))}
              </div>
              {blockedCustomers.length > 0 && <div className="alert alert-error" style={{ marginTop: 12 }}>Explicitly skip ambiguous rows or correct invalid source rows. No partial batch will be saved.</div>}
              {customerCounts.create + customerCounts.reuse === 0 && <div className="field-hint" style={{ marginTop: 12 }}>At least one safe customer row is required to import.</div>}
              {reviewedCustomers.length > 200 && <div className="alert alert-error">More than 200 customer rows need a separately reviewed batch. No partial batch was approved.</div>}
              <label className="field-hint" style={{ display: 'block', marginTop: 12 }}>
                <input type="checkbox" checked={customerReviewed} onChange={e => setCustomerReviewed(e.target.checked)} disabled={blockedCustomers.length > 0 || profile?.role !== 'owner'} />
                {' '}I reviewed every customer row and the ignored columns.
              </label>
              <button className="btn btn-green" onClick={importCustomerBatch} disabled={!canImportCustomers} style={{ marginTop: 12 }}>
                {customerImporting ? 'Saving...' : `Import ${customerCounts.create + customerCounts.reuse} Customers (${customerCounts.skip} skipped)`}
              </button>
              {profile?.role !== 'owner' && <div className="field-hint" style={{ marginTop: 8 }}>Only the workshop owner can commit a customer batch.</div>}
            </div>
          )}
        </section>

        <section className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-title">Historical Jobs</div>
          <div className="legacy-import-note">Historical references are searchable records, not active jobs. No services, invoices, payments or workflow are inferred.</div>
          <div className="grid-2">
            <div className="field"><label>Upload old-job CSV or TSV</label><input type="file" accept=".csv,.tsv,.txt" onChange={handleHistoricFile} />
              {historicFileName && <div className="text-muted fs12">Loaded: {historicFileName}</div>}</div>
            <div className="field"><label>Paste old-job data</label><textarea rows={3} value={historicText}
              onChange={e => { setHistoricText(e.target.value); setHistoricPreview(null); setHistoricReviewed(false); setHistoricError('') }} /></div>
          </div>
          <div className="action-row"><button className="btn btn-primary" onClick={() => previewHistoricJobs(historicText)}
            disabled={!historicText.trim() || historicImporting}>Preview Historical Jobs</button></div>
          {historicError && <div className="alert alert-error">{historicError}</div>}
          {!historicMatchesReady && <div className="alert alert-error">Existing historical matches are incomplete. Import is blocked until the v104 schema is live and records can be loaded.</div>}
          {historicResult && <HistoricImportLinkReview result={historicResult} records={historicRecords}
            owner={profile?.role === 'owner'} onReview={setViewHistoricId} onRetry={load} />}
          <LegacyAiAssist mode="historic_jobs" title="Historical Jobs" workshopId={workshopId}
            sourceText={historicText} columns={HISTORIC_FIELDS} validateRows={validateHistoricProposal}
            onDirectPreview={(text, nextFileName) => previewHistoricJobs(text, nextFileName || historicFileName)}
            onUse={rows => previewHistoricJobs(proposalCsv(HISTORIC_FIELDS, rows), 'AI historical proposal',
              rows.map(row => row.row_number), rows)} />
          {historicPreview && <div className="legacy-preview-card">
            <div className="card-title">Historical Preview</div>
            <div className="text-muted fs12">Original rows: {reviewedHistoric.length}. {historicCounts.create} create · {historicCounts.reuse} reuse · {historicCounts.skip} skip · {historicCounts.blocked} blocked.</div>
            <div className="field-hint">Ignored fields: {historicPreview.ignoredColumns.join(', ') || 'none'}.</div>
            <div className="legacy-preview-table">{reviewedHistoric.map(row => <div className="legacy-preview-row" key={row.rowNumber}>
              <div className="legacy-preview-row-main"><span className="job-status-chip">Source row {row.rowNumber}</span>
                <strong>{row.facts.id}</strong><span>{historicalOriginalCustomer(row.facts)}</span>
                <span>{row.facts.status || 'No status'}</span><span>{row.action}</span></div>
              {(row.facts.invoice_items || row.facts.historical_amount) &&
                <div className="field-hint">Invoice facts: {row.facts.invoice_items || 'No items supplied'}
                  {row.facts.historical_amount && ` / ${row.facts.currency || ''} ${row.facts.historical_amount}`}</div>}
              {(row.ignoredFields.length > 0 || row.uncertainties.length > 0) &&
                <div className="field-hint">Ignored: {row.ignoredFields.join(', ') || 'none'}.
                  {' '}Uncertainties: {row.uncertainties.join('; ') || 'none'}.</div>}
              {(() => {
                const match = historicalCustomerCandidates(row.facts, customers)
                return <div className="field-hint">{match.kind === 'contact' ? 'Contact candidate - owner must confirm link after import.'
                  : match.kind === 'name_only' ? 'Name-only candidate - no automatic link.'
                  : match.kind === 'ambiguous' ? 'Conflicting identity evidence - review before linking.'
                  : 'No customer candidate; reference remains searchable and unlinked.'}
                  {match.candidates.length > 0 && ` Suggested: ${match.candidates.map(customerName).join(', ')}`}</div>
              })()}
              <details className="legacy-historic-source"><summary>Source and proposed fields</summary>
                <div className="grid-2"><div>{Object.entries(row.sourceFields || row.raw || {}).map(([field, value]) =>
                  <div className="detail-row" key={field}><span>{field}</span><span>{value || 'Empty'}</span></div>)}</div>
                  <div>{HISTORIC_FIELDS.map(field => <div className="detail-row" key={field}>
                    <span>{field.replace(/_/g, ' ')}</span><span>{row.facts[field] || 'Not mapped'}</span>
                  </div>)}</div></div>
              </details>
              {row.errors.map(message => <div className="alert alert-error" key={message}>{message}</div>)}
              {row.conflict && <label className="field-hint"><input type="checkbox" checked={row.skip}
                onChange={e => { setHistoricSkipped(current => e.target.checked
                  ? [...current, row.rowNumber] : current.filter(number => number !== row.rowNumber)); setHistoricReviewed(false) }} />
                Skip conflicting source row</label>}
            </div>)}</div>
            {reviewedHistoric.length > 200 && <div className="alert alert-error">More than 200 rows need a separately reviewed import batch. Nothing was partially approved.</div>}
            <label className="field-hint"><input type="checkbox" checked={historicReviewed}
              onChange={e => setHistoricReviewed(e.target.checked)} disabled={!!historicBlocked.length || profile?.role !== 'owner'} />
              I reviewed every historical row and ignored field.</label>
            <button className="btn btn-green" onClick={importHistoricBatch} disabled={!canImportHistoric}>
              {historicImporting ? 'Saving...' : 'Import Historical References'}</button>
          </div>}
          <div className="card-title">Search Historical References</div>
          <div className="field-hint">{historicRecords.length} historical references:
            {' '}{linkedHistoricCount} linked, {historicRecords.length - linkedHistoricCount} unlinked.
            Unlinked references stay searchable here but do not appear under a customer.</div>
          <input value={historicSearch} onChange={e => setHistoricSearch(e.target.value)} placeholder="Search source job ID, customer or status" />
          <div className="legacy-preview-table">{historicRecords.filter(record =>
            !historicSearch || JSON.stringify(record.facts).toLowerCase().includes(historicSearch.toLowerCase())
              || record.source_reference.toLowerCase().includes(historicSearch.toLowerCase())).map(record =>
            <button type="button" className="legacy-preview-row legacy-historic-result" key={record.id}
              onClick={() => setViewHistoricId(record.id)}><strong>{record.source_reference}</strong>
              <span> {historicalOriginalCustomer(record.facts)}</span>
              <span> {record.facts.status || 'Historical reference'}</span>
              <span>{record.customer_id ? 'Linked' : 'Unlinked'}</span>
              <span>{profile?.role === 'owner' && !record.customer_id
                ? 'Review customer link' : 'Open historical detail'}</span></button>)}</div>
        </section>
      </div>

      {viewHistoricId && historicRecords.find(record => record.id === viewHistoricId) &&
        <LegacyHistoricJobModal record={historicRecords.find(record => record.id === viewHistoricId)}
          customers={customers} owner={profile?.role === 'owner'}
          onClose={() => setViewHistoricId(null)}
          onLinked={updated => setHistoricRecords(current => current.map(record =>
            record.id === updated.id ? updated : record))} />}

      {enrichedRows.length > 0 && (
        <section className="card legacy-preview-card">
          <div className="legacy-preview-head">
            <div>
              <div className="card-title" style={{ marginBottom: 4 }}>Preview</div>
              <div className="text-muted fs12">
                {rowStats.total} rows - {rowStats.valid} valid - {rowStats.warning} warnings - {rowStats.error} errors
              </div>
            </div>
            <button className="btn btn-green" onClick={importPreviewRows} disabled={!canImport}>
              {importing ? 'Saving...' : `Import ${rowStats.total} Service References`}
            </button>
          </div>
          {rowStats.error > 0 && <div className="alert alert-error" style={{ marginBottom: 12 }}>Fix rows with errors before importing.</div>}
          {enrichedRows.length > 200 && <div className="alert alert-error">More than 200 service rows need a separately reviewed batch. Nothing was partially approved.</div>}
          <label className="field-hint"><input type="checkbox" checked={serviceReviewed}
            onChange={e => setServiceReviewed(e.target.checked)} disabled={rowStats.error > 0 || profile?.role !== 'owner'} />
            I reviewed every performed-service fact, customer match and ignored field.</label>
          <div className="legacy-preview-table">
            {enrichedRows.map(row => {
              const service = row.normalised.service
              const customer = row.matchedCustomer || row.normalised.customer

              return (
                <div key={row.rowNumber} className={`legacy-preview-row legacy-preview-${row.status}`}>
                  <div className="legacy-preview-row-main">
                    <StatusPill tone={row.status === 'error' ? 'orange' : row.status === 'warning' ? 'muted' : 'green'}>Row {row.rowNumber}</StatusPill>
                    <strong>{customerName(customer)}</strong>
                    <span>{service.service_date ? fmtDate(service.service_date) : service.service_date_text || 'No date'}</span>
                    <span>{serviceSummary(service)}</span>
                    {service.amount_charged !== null && <span>{row.raw.currency || 'Currency unknown'} {Number(service.amount_charged).toFixed(2)}</span>}
                  </div>
                  <div className="legacy-preview-row-meta">
                    <span>{row.matchedCustomer ? 'Link existing customer' : 'Create customer'}</span>
                    <span>Bike/component source facts remain reference text; no active bike or component is created.</span>
                    <span>{row.sourceMatch ? 'Reuse matching source reference' : 'New source reference'}</span>
                  </div>
                  {(row.errors.length > 0 || row.warnings.length > 0) && (
                    <div className="legacy-preview-row-warnings">
                      {[...row.errors, ...row.warnings].map((message, index) => <span key={index}>{message}</span>)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      <section className="card">
        <div className="card-title">Searchable Legacy History</div>
        {filteredRecords.length === 0 ? (
          <div className="muted-empty">{records.length ? 'No legacy records match that search.' : 'No legacy history imported yet.'}</div>
        ) : (
          <div className="legacy-history-list">
            {filteredRecords.slice(0, 80).map(record => (
              <div key={record.id} className="legacy-history-row">
                <div>
                  <div className="legacy-history-title">
                    <span>{record.service_date ? fmtDate(record.service_date) : record.service_date_text || 'No date'}</span>
                    <strong>{serviceSummary(record)}</strong>
                    <span className="job-status-chip job-status-muted">Legacy history</span>
                  </div>
                  <div className="legacy-history-meta">
                    <span>{customerName(record.customer)}</span>
                    {record.bike && <span>{bikeName(record.bike)}</span>}
                    {record.component && <span>{componentName(record.component)}</span>}
                    {record.source_system && <span>{record.source_system}</span>}
                    {record.source_reference && <span>{record.source_reference}</span>}
                  </div>
                  {(record.service_description || record.notes || record.setup_text) && (
                    <div className="legacy-history-copy">{[record.service_description, record.notes, record.setup_text].filter(Boolean).join(' - ')}</div>
                  )}
                </div>
                {record.amount_charged !== null && record.amount_charged !== undefined && (
                  <div className="legacy-history-amount">{record.currency || 'Currency unknown'} {Number(record.amount_charged).toFixed(2)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
